import { editorFileNameForLanguage } from './EditorBreadcrumb';
import { normalizeLanguage } from './languageTemplates';

/** Extension → language (Turbo C / VS Code style). */
const EXTENSION_TO_LANGUAGE = {
  c: 'c',
  h: 'c',
  cpp: 'cpp',
  cc: 'cpp',
  cxx: 'cpp',
  hpp: 'cpp',
  py: 'python',
  java: 'java',
  js: 'javascript',
  mjs: 'javascript',
  ts: 'typescript',
  html: 'html',
  htm: 'html',
  css: 'css',
};

const LANGUAGE_TO_EXTENSION = {
  c: '.c',
  cpp: '.cpp',
  python: '.py',
  java: '.java',
  javascript: '.js',
  typescript: '.ts',
  html: '.html',
  css: '.css',
};

/**
 * Entries shown in the Windows "Save as type" dropdown.
 * Keep MIME types simple — Chromium rejects odd/overlapping accept maps.
 */
const ALL_SAVE_TYPES = [
  { id: 'c', description: 'C Source (*.c)', accept: { 'text/x-c': ['.c'] } },
  { id: 'cpp', description: 'C++ Source (*.cpp)', accept: { 'text/x-c++src': ['.cpp'] } },
  { id: 'java', description: 'Java (*.java)', accept: { 'text/x-java-source': ['.java'] } },
  { id: 'python', description: 'Python (*.py)', accept: { 'text/x-python': ['.py'] } },
  { id: 'html', description: 'HTML (*.html)', accept: { 'text/html': ['.html', '.htm'] } },
  { id: 'css', description: 'CSS (*.css)', accept: { 'text/css': ['.css'] } },
  { id: 'javascript', description: 'JavaScript (*.js)', accept: { 'text/javascript': ['.js'] } },
];

const ALL_EXTENSIONS = Object.keys(EXTENSION_TO_LANGUAGE).map((ext) => `.${ext}`);

const SOURCE_OPEN_TYPES = [
  {
    description: 'Source code',
    accept: {
      'text/plain': ALL_EXTENSIONS,
    },
  },
];

export function supportsNativeOpenPicker() {
  return typeof window !== 'undefined' && typeof window.showOpenFilePicker === 'function';
}

export function supportsNativeSavePicker() {
  return typeof window !== 'undefined' && typeof window.showSaveFilePicker === 'function';
}

/** @deprecated Prefer supportsNativeOpenPicker / supportsNativeSavePicker */
export function supportsNativeFilePicker() {
  return supportsNativeOpenPicker() && supportsNativeSavePicker();
}

export function inferLanguageFromFileName(fileName) {
  const match = String(fileName || '').toLowerCase().match(/\.([a-z0-9]+)$/);
  if (!match) return null;
  return EXTENSION_TO_LANGUAGE[match[1]] || null;
}

export function suggestedFileName(language, customName) {
  if (customName?.trim()) return customName.trim();
  return editorFileNameForLanguage(language);
}

export function extensionForLanguage(language) {
  return LANGUAGE_TO_EXTENSION[normalizeLanguage(language)] || '.txt';
}

function triggerDownload(content, fileName) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function readFileFromInput(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error('Could not read the selected file.'));
    reader.readAsText(file);
  });
}

function resolveSaveSuggestedName(language, fileName) {
  const suggested = fileName || suggestedFileName(language);
  const ext = extensionForLanguage(language);
  if (suggested.toLowerCase().endsWith(ext)) return suggested;
  return `${String(suggested).replace(/\.[^.]+$/, '')}${ext}`;
}

/** Current language first so Windows "Save as type" defaults correctly. */
function savePickerTypesForLanguage(language) {
  const lang = normalizeLanguage(language);
  const primary = ALL_SAVE_TYPES.filter((t) => t.id === lang);
  const rest = ALL_SAVE_TYPES.filter((t) => t.id !== lang);
  return [...primary, ...rest].map(({ description, accept }) => ({ description, accept }));
}

/**
 * Open Windows Save As (Chromium File System Access API) and write content.
 * Returns null if the user cancels.
 */
async function writeWithNativeSavePicker({ code, language, fileName }) {
  const suggestedName = resolveSaveSuggestedName(language, fileName);
  const attempts = [
    {
      suggestedName,
      types: savePickerTypesForLanguage(language),
      excludeAcceptAllOption: false,
    },
    {
      suggestedName,
      types: [
        {
          description: 'Text file',
          accept: { 'text/plain': [extensionForLanguage(language)] },
        },
      ],
    },
    { suggestedName },
  ];

  let lastError = null;
  for (const options of attempts) {
    try {
      const handle = await window.showSaveFilePicker(options);
      const writable = await handle.createWritable();
      await writable.write(code);
      await writable.close();
      return {
        fileName: handle.name,
        fileHandle: handle,
        method: 'fsa',
      };
    } catch (err) {
      if (err?.name === 'AbortError') return null;
      lastError = err;
      if (err?.name === 'TypeError' || /accept|type/i.test(String(err?.message || ''))) {
        continue;
      }
      throw err;
    }
  }
  throw lastError || new Error('Could not open the Windows Save As dialog.');
}

/**
 * Open a source file from the student's device (not cloud).
 * Uses File System Access API on Chromium desktop; file input elsewhere.
 */
export async function openLocalSourceFile() {
  if (supportsNativeOpenPicker()) {
    try {
      const [handle] = await window.showOpenFilePicker({
        multiple: false,
        types: SOURCE_OPEN_TYPES,
      });
      const file = await handle.getFile();
      const code = await file.text();
      const language = inferLanguageFromFileName(file.name);
      return {
        code,
        fileName: file.name,
        language,
        fileHandle: handle,
        method: 'fsa',
      };
    } catch (err) {
      if (err?.name === 'AbortError') return null;
      throw err;
    }
  }

  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ALL_EXTENSIONS.join(',');
    input.style.display = 'none';
    document.body.appendChild(input);

    const cleanup = () => {
      input.remove();
    };

    input.addEventListener('change', async () => {
      const file = input.files?.[0];
      cleanup();
      if (!file) {
        resolve(null);
        return;
      }
      try {
        const code = await readFileFromInput(file);
        resolve({
          code,
          fileName: file.name,
          language: inferLanguageFromFileName(file.name),
          fileHandle: null,
          method: 'input',
        });
      } catch (err) {
        resolve({ error: err?.message || 'Could not open file.' });
      }
    });

    input.addEventListener('cancel', () => {
      cleanup();
      resolve(null);
    });

    input.click();
  });
}

/**
 * Save to an existing file handle (desktop Chromium) or open Save As.
 */
export async function saveLocalSourceFile({ code, fileName, fileHandle, language }) {
  const safeName = fileName || 'program.txt';

  if (fileHandle && typeof fileHandle.createWritable === 'function') {
    try {
      const writable = await fileHandle.createWritable();
      await writable.write(code);
      await writable.close();
      return { fileName: safeName, fileHandle, method: 'fsa' };
    } catch (err) {
      if (err?.name === 'NotAllowedError') {
        return saveLocalSourceFileAs({ code, language, fileName: safeName });
      }
      throw err;
    }
  }

  return saveLocalSourceFileAs({ code, language, fileName: safeName });
}

/**
 * Always open destination picker — Windows Save As on Chrome/Edge.
 * On Firefox / Safari returns { method: 'unsupported' } so the UI can show
 * a fallback dialog instead of silently dumping into Downloads.
 */
export async function saveLocalSourceFileAs({
  code,
  language,
  fileName,
  allowDownloadFallback = false,
}) {
  if (supportsNativeSavePicker()) {
    return writeWithNativeSavePicker({ code, language, fileName });
  }

  if (!allowDownloadFallback) {
    return {
      fileName: resolveSaveSuggestedName(language, fileName),
      fileHandle: null,
      method: 'unsupported',
      language,
      code,
    };
  }

  const finalName = resolveSaveSuggestedName(language, fileName);
  triggerDownload(code, finalName);
  return { fileName: finalName, fileHandle: null, method: 'download' };
}

/** Explicit download (used after Firefox fallback dialog confirms). */
export function downloadLocalSourceFile(code, fileName) {
  const finalName = fileName || 'program.txt';
  triggerDownload(code, finalName);
  return { fileName: finalName, fileHandle: null, method: 'download' };
}

export function formatFileStatusMessage(result) {
  if (!result) return '';
  if (result.method === 'fsa') {
    return `Saved to ${result.fileName} on your device.`;
  }
  if (result.method === 'download') {
    return `Downloaded ${result.fileName} (check your browser Downloads folder).`;
  }
  if (result.method === 'unsupported') {
    return '';
  }
  return `Saved ${result.fileName}.`;
}
